import {RichUtils} from 'draft-js';
import lodash from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import {components} from 'react-select';

import {customFontStyles} from '../../constants/styleContants';
import Select from '../../../../common/select/Select';

import './fontSelect.scss';

/**
 * The list of style buttons before sorting.
 *
 * @type {Array.<{label: string, value: string}>}
 */
const RAW_FONT_VALUES = lodash.map(customFontStyles, (fontData, styleName) => {
  const fontName = fontData.name || styleName.replace('FONT_', '');
  const fontFamily = fontData.fontFamily;
  return {label: fontName, value: styleName, fontFamily};
});

/**
 * The list of style buttons.
 *
 * @type {Array.<{label: string, value: string}>}
 */
const FONT_VALUES = lodash.sortBy(RAW_FONT_VALUES, (fontValue) => {
  return fontValue.label;
});

/**
 * A custom option style for the font select dropdown.
 *
 * @param {{data: {fontFamily: string}}} props
 * @returns {React.Component}
 */
const RenderOption = (props) => {
  const {data, innerProps} = props;
  const newInnerProps = {
    ...innerProps,
    style: {fontFamily: data.fontFamily}
  };
  return (
    <components.Option {...props} innerProps={newInnerProps} />
  );
};

RenderOption.propTypes = {
  data: PropTypes.shape({fontFamily: PropTypes.string}),
  innerProps: PropTypes.object,
};

/**
 * The FontSelect component.
 *
 * @param {{}} params
 * @param {{}} params.editorState
 * @param {function(EditorState)} params.onChangeTextState
 * @param {function()=} params.beforeChange
 * @returns {React.Component}
 */
export const FontSelect = ({editorState, onChangeTextState, beforeChange}) => {
  const currentStyle = editorState.getCurrentInlineStyle();

  let currentValue = '';
  FONT_VALUES.forEach((fontOption) => {
    if (currentStyle.has(fontOption.value)) {
      currentValue = fontOption.value;
      return false;
    }
    return true;
  });

  /**
   * Updates the editor state with a new style when a button is clicked.
   *
   * @param {string} changeStyle
   */
  const onChange = (changeStyle) => {
    let changeState = editorState;
    if (beforeChange) {
      changeState = beforeChange();
    }

    const beforeChangeStyle = changeState.getCurrentInlineStyle();

    // First remove any fonts currently set.
    let newEditorState = changeState;
    FONT_VALUES.forEach((fontOption) => {
      if (beforeChangeStyle.has(fontOption.value)) {
        newEditorState = RichUtils.toggleInlineStyle(newEditorState, fontOption.value);
      } else {
        /*
         * Because of a bug in detecting active styles in mixed blocks, we need to turn on this font everywhere,
         * and then off again. This will make sure all the currently set fonts are removed.
         */
        newEditorState = RichUtils.toggleInlineStyle(newEditorState, fontOption.value);
        newEditorState = RichUtils.toggleInlineStyle(newEditorState, fontOption.value);
      }
    });

    // Now add the new font.
    newEditorState = RichUtils.toggleInlineStyle(newEditorState, changeStyle || '');
    onChangeTextState(newEditorState);
  };

  return (
    <div className="font-select">
      <Select
        theme="dark"
        onChange={onChange}
        options={FONT_VALUES}
        value={currentValue}
        components={{Option: RenderOption}}
      />
    </div>
  );
};

FontSelect.propTypes = {
  editorState: PropTypes.object.isRequired,
  onChangeTextState: PropTypes.func.isRequired,

  beforeChange: PropTypes.func,
};

export default FontSelect;
